import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import trackEvent from 'lib/posthog/trackEvent';
import { userQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import { getUserOfferRatings, postOfferReaction } from 'services/offer';

// Maps internal vote type to display label (used for event label generation)
const VOTE_TYPE_TO_LABEL = { GREAT: 'like', DISLIKE: 'dislike' };

// Maps internal vote type to the API value the backend accepts.
// 'DISLIKE' maps to 'BAD' — backend OfferRatingType::TYPE_IDS only
// has 'BAD' for dislike (not 'DISLIKE').
const VOTE_TYPE_TO_API = { GREAT: 'GREAT', DISLIKE: 'BAD' };

/**
 * Handles offer like/dislike via POST /v3/offers/{id}/ratings.
 * Fetches the user's existing reactions from GET /v4/users/{id}/offer-ratings
 * to pre-fill the initial vote state on page load.
 * Fires trackEvent only after a successful API response (order is enforced).
 *
 * @param {number} offerId
 */
const useOfferRating = (offerId) => {
  const queryClient = useQueryClient();
  const { isLoggedIn, LoggedInUser, runCallbackIfLoggedIn } = useUser();

  const { data: ratingsMap = {}, isFetched } = useQuery({
    queryKey: userQueryKeys.offerRatings(),
    queryFn: ({ signal }) =>
      getUserOfferRatings({ userId: LoggedInUser?.userId }, { signal }),
    enabled: isLoggedIn && !!LoggedInUser?.userId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const initialVote = ratingsMap[offerId] ?? null;
  // useState so consumers re-render on revert — pure cache-read
  // wouldn't propagate failure back to the UI.
  const [sessionVote, setSessionVote] = useState(initialVote);
  const pendingRef = useRef(false);

  useEffect(() => {
    setSessionVote(initialVote);
  }, [initialVote]);

  // Frozen snapshot of the server vote at first query resolution.
  // Used by ReactionGroup for count normalisation — must not change
  // when the user votes, otherwise initialDelta cancels currentDelta
  // (self-canceling bug). Only freeze once isFetched — premature
  // freeze to null masks a returning voter's pre-existing vote.
  const serverVoteRef = useRef(undefined);
  if (serverVoteRef.current === undefined && isFetched) {
    serverVoteRef.current = initialVote;
  }

  // Ref so the async callback reads the latest value without stale closure.
  const sessionVoteRef = useRef(sessionVote);
  sessionVoteRef.current = sessionVote;

  const handleVote = useCallback(
    (type) => {
      runCallbackIfLoggedIn(async () => {
        if (pendingRef.current) return;
        pendingRef.current = true;
        const label = VOTE_TYPE_TO_LABEL[type];
        const prevVote = sessionVoteRef.current;
        const isUnsetting = prevVote === label;
        const eventLabel = `${label}_${isUnsetting ? 'unset' : 'set'}`;
        const nextVote = isUnsetting ? null : label;

        // Phase order is load-bearing: optimistic update before await,
        // cache write after success, revert in catch.
        setSessionVote(nextVote);

        try {
          await postOfferReaction({
            offerId,
            reaction: VOTE_TYPE_TO_API[type],
          });

          // Keep cache in sync so initialVote is correct on remount.
          queryClient.setQueryData(
            userQueryKeys.offerRatings(),
            (prev = {}) => {
              const next = { ...(prev ?? {}) };
              if (isUnsetting) delete next[offerId];
              else next[offerId] = label;
              return next;
            }
          );

          trackEvent(
            'click home offer reaction',
            {
              event_category: 'home',
              event_label: eventLabel,
              offer_id: offerId,
            },
            { destination: 'ga4' }
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[useOfferRating] vote failed', err);
          setSessionVote(prevVote);
        } finally {
          pendingRef.current = false;
        }
      });
    },
    [offerId, runCallbackIfLoggedIn]
  );

  return {
    handleVote,
    currentVote: sessionVote,
    serverVote: serverVoteRef.current,
  };
};

export default useOfferRating;
